import React, {
  useEffect, useMemo, useRef, useState, useSyncExternalStore,
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreateAccount,
  useAccountsList,
  useEditRegistration,
  useUpdateRegistrationByToken,
} from '../../../hooks/account';
// import { useCreateCompanyFournisseur } from '~/hooks/companies';
import {
  InputText,
  StyledSelect,
  // CreatableSelect,
  Checkbox,
  InputEmail,
  ErrorField,
} from '~/lib/HooksFormFields';
import { IUser } from '~/types/users';
import getErrorAuthMessage from '~/utils/auth-helpers';
import styles from './register-form.module.scss';
import PresencesForm from './PresencesForm';
import EventsForm from './EventsForm';
import CollaboratorsForm from './CollaboratorsForm';
import {
  userFunctions,
  CONTACT_EMAIL,
  PROFILE_FIRST_LAST_NAME_MAX_LENGTH,
} from '~/constants';
import charteImage from '../../../assets/images/charte.jpg';
import DisplaySettings from '~/components/DisplaySettings';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const MAGASIN_CHARTER_LABELS = [
  (
    <>
      <span className={styles.bold}>Je m’engage à tester uniquement sur place</span>
      {' '}
      les produits<br/>proposés pour travailler mon assortiment.
    </>
  ),
  (
    <>
      <span className={styles.bold}>Je m’engage donc à ne pas emporter de produits</span>
      {' '}
      avec<br/>moi hors du salon.
    </>
  ),
] as const;

const FOURNISSEUR_CHARTER_LABELS = [
  (
    <>
      <span className={styles.bold}>Je m’engage à faire tester uniquement sur place</span><br/>
      {' '}
      mes produits aux visiteurs du salon.
    </>
  ),
  (
    <>
      <span className={styles.bold}>Je m’engage à ne distribuer aucun produit,</span><br/>
      {' '}
      ni sac publicitaire aux visiteurs.
    </>
  ),
  (
    <>
      <span className={styles.bold}>Je m’engage à faire don aux associations</span>
      {' '}
      partenaires<br/>
      {' '}
      <span className={styles.bold}>et à ne pas troquer</span>
      {' '}
      de produits avec d’autres exposants.
    </>
  ),
] as const;

type RecaptchaHandle = {
  getValue: () => string | undefined;
  reset: () => void;
};

function useSalonDarkMode(): boolean {
  return useSyncExternalStore(
    (onStoreChange) => {
      const obs = new MutationObserver(() => onStoreChange());
      obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      return () => obs.disconnect();
    },
    () => document.body.classList.contains('dark-mode'),
    () => false,
  );
}

type ICollaborators = Partial<IUser>[];

const RegisterForm = () => {
  const { type, token: editToken } = useParams();
  const isEditMode = !!editToken;
  const navigate = useNavigate();
  type CollaboratorsFormHandle = React.ElementRef<typeof CollaboratorsForm>;
  type EventsFormHandle = React.ElementRef<typeof EventsForm>;
  const collaboratorsForm = useRef<CollaboratorsFormHandle>(null);
  const eventsFormRef = useRef<EventsFormHandle>(null);
  const recaptchaRef = useRef<RecaptchaHandle>(null);
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const [charterAccepted, setCharterAccepted] = useState(false);
  const isDarkMode = useSalonDarkMode();

  const needsCharterAcceptance = !isEditMode && (type === 'magasin' || type === 'fournisseur');

  // const createCompanyFournisseur = useCreateCompanyFournisseur();
  // const { isLoading: createCompanyLoading } = createCompanyFournisseur;

  const {
    mutate: createAccount, error, isSuccess, isLoading, data: user,
  } = useCreateAccount();
  const {
    mutateAsync: updateRegistrationByToken,
    isLoading: isLoadingUpdate,
    error: updateError,
  } = useUpdateRegistrationByToken();
  const { data: editRegistrationData } = useEditRegistration(editToken || '');

  const errorApi: any = error;
  const updateErrorApi: any = updateError;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<FieldValues>({
    defaultValues: {
      charterCommit1: false,
      charterCommit2: false,
      charterCommit3: false,
    },
    // mode: 'onChange',
  });

  const watchExhibition = watch('exhibition');
  const watchEmail = watch('email');
  const watchCompany = watch('company');
  const watchRegion = watch('region');
  const watchSign = watch('sign');
  const watchFirstName = watch('profile.firstName');
  const watchLastName = watch('profile.lastName');
  const watchUserFunction = watch('userFunction');
  const watchPresences = watch('presences');
  const charterCommit1 = watch('charterCommit1');
  const charterCommit2 = watch('charterCommit2');
  const charterCommit3 = watch('charterCommit3');
  const allCharterChecks = useMemo(() => {
    if (type === 'magasin') {
      return !!(charterCommit1 && charterCommit2);
    }
    if (type === 'fournisseur') {
      return !!(charterCommit1 && charterCommit2 && charterCommit3);
    }
    return true;
  }, [type, charterCommit1, charterCommit2, charterCommit3]);

  const {
    data: list,
  } = useAccountsList({
    type,
    exhibition: watchExhibition,
  });
  const {
    exhibitions = [],
    companies = [],
    days = [],
    formations = [],
    regions = [],
    signs = [],
  } = list;

  // const handleCreateCompanyFournisseur = async (value: string) => {
  //   if (value) {
  //     const data = await createCompanyFournisseur.mutateAsync({ name: value });
  //     if (data?.company._id) {
  //       setValue('company', data?.company._id);
  //     }
  //   }
  // };

  const submit = async (data: FieldValues) => {
    if (needsCharterAcceptance && !charterAccepted) {
      return;
    }
    const {
      email,
      exhibition,
      optin,
      company = null,
      sign,
      region,
      presences,
      charterCommit1: _c1,
      charterCommit2: _c2,
      charterCommit3: _c3,
      ...mainUser
    } = data;

    let formationSlots: string[] = [];
    if (type === 'magasin' && eventsFormRef?.current) {
      formationSlots = eventsFormRef.current.getEvents() || [];
    }

    const formatedData: any = {
      email,
      // optin,
      company,
      exhibitions: exhibition === 'all' ? [...exhibitions.map((d:any) => d.value)] : [exhibition],
      sign,
      region,
      users: [{
        mainUser: true,
        ...mainUser,
        isCollaborator: type === 'collaborateur',
        presences: data.presences,
        formationSlots,
      }],
    };

    if (type !== 'collaborateur' && collaboratorsForm?.current) {
      let collaborators: ICollaborators = collaboratorsForm.current.getCollaborators() || [];
      collaborators = collaborators.map((collaborator: any) => ({
        ...collaborator,
        presences: collaborator.presences,
      }));
      formatedData.users = [
        ...formatedData.users,
        ...collaborators,
      ];
    }

    if (isEditMode && editToken) {
      const response: any = await updateRegistrationByToken({
        token: editToken,
        payload: formatedData,
      });
      const mainUserId = response?.mainUserId || editRegistrationData?.registration?.mainUser?._id;
      if (mainUserId) {
        navigate(`/confirm/${mainUserId}`);
      } else {
        navigate('/login');
      }
    } else {
      if (RECAPTCHA_SITE_KEY) {
        const recaptchaResponse = recaptchaRef.current?.getValue();
        if (!recaptchaResponse) {
          setCaptchaError('Veuillez cocher la case « Je ne suis pas un robot ».');
          return;
        }
        formatedData.recaptchaToken = recaptchaResponse;
      }
      setCaptchaError('');
      await createAccount(formatedData);
    }
  };

  const errorApiMessage = useMemo(() => {
    if (!error && !updateError) return '';
    const currentErrorApi = updateErrorApi || errorApi;
    const apiError = currentErrorApi?.response?.data?.error || currentErrorApi?.response?.data;
    const msg = getErrorAuthMessage(apiError);
    return msg;
  }, [errorApi, updateErrorApi, error, updateError]);

  const missingRequiredFieldsLabel = useMemo(() => {
    const labelByPath: Record<string, string> = {
      exhibition: type === 'collaborateur' ? 'Activité' : 'Salon',
      company: type === 'magasin' ? 'Magasin' : 'Société',
      region: 'Région',
      sign: 'Enseigne',
      email: 'Email',
      'profile.firstName': 'Prénom',
      'profile.lastName': 'Nom',
      userFunction: 'Fonction',
      presence: 'Présence',
      lunch: 'Repas',
    };

    const getMissingFromWatched = () => {
      const missing: string[] = [];

      const require = (path: keyof typeof labelByPath, isMissing: boolean) => {
        if (!isMissing) return;
        missing.push(labelByPath[path] || String(path));
      };

      if (type !== 'magasin') {
        require('exhibition', !watchExhibition);
      }
      require('email', !watchEmail);
      require('profile.firstName', !watchFirstName);
      require('profile.lastName', !watchLastName);
      require('userFunction', !watchUserFunction);

      if (type === 'magasin') {
        require('region', !watchRegion);
        require('sign', !watchSign);
      }

      if (type !== 'collaborateur') {
        require('company', !watchCompany);
      }

      const presencesArray = Array.isArray(watchPresences) ? watchPresences : [];
      const presenceMissing = presencesArray.length === 0
        || presencesArray.some((p: any) => typeof p?.present !== 'boolean');
      require('presence', presenceMissing);

      const lunchMissing = presencesArray.some((p: any) => p?.present === true && typeof p?.lunch !== 'boolean');
      require('lunch', lunchMissing);

      return Array.from(new Set(missing)).join(', ');
    };

    const collectPaths = (node: any, prefix = ''): string[] => {
      if (!node || typeof node !== 'object') return [];
      if (typeof node?.message === 'string') return [prefix];
      return Object.entries(node).flatMap(([key, value]) => {
        if (key === 'ref' || key === 'type' || key === 'message') return [];
        const nextPrefix = prefix ? `${prefix}.${key}` : key;
        return collectPaths(value, nextPrefix);
      });
    };

    const paths = collectPaths(errors);
    const labels = paths
      .map((p) => labelByPath[p] || p)
      .filter(Boolean);

    const fromErrors = Array.from(new Set(labels)).join(', ');
    return fromErrors || getMissingFromWatched();
  }, [
    errors,
    type,
    watchExhibition,
    watchEmail,
    watchCompany,
    watchRegion,
    watchSign,
    watchFirstName,
    watchLastName,
    watchUserFunction,
    watchPresences,
  ]);

  const submitDisabledReason = useMemo(() => {
    if (isLoading || isLoadingUpdate) {
      return 'Envoi en cours…';
    }
    if (!isValid) {
      const details = missingRequiredFieldsLabel
        ? `Champs obligatoires manquants : ${missingRequiredFieldsLabel}.`
        : 'Veuillez compléter tous les champs obligatoires et cocher la case relative à la gestion des données.';
      return details;
    }
    if (needsCharterAcceptance && !charterAccepted) {
      return 'Veuillez accepter la charte pour continuer.';
    }
    if (!isEditMode && !!RECAPTCHA_SITE_KEY && !recaptchaVerified) {
      return 'Veuillez cocher la case « Je ne suis pas un robot ».';
    }
    return '';
  }, [
    isLoading,
    isLoadingUpdate,
    isValid,
    missingRequiredFieldsLabel,
    isEditMode,
    recaptchaVerified,
    needsCharterAcceptance,
    charterAccepted,
  ]);

  const isSubmitDisabled = !!submitDisabledReason;

  const optionsCompanies = useMemo(
    () => companies.filter((d: any) => d.type === type),
    [type, companies],
  );

  useEffect(() => {
    if (user?.account?.mainUser) {
      navigate(`/confirm/${user?.account?.mainUser}`);
    }
  }, [user, navigate]);

  useEffect(() => {
    if (error && !isEditMode && RECAPTCHA_SITE_KEY) {
      recaptchaRef.current?.reset();
      setRecaptchaVerified(false);
    }
  }, [error, isEditMode]);

  useEffect(() => {
    if (!allCharterChecks) {
      setCharterAccepted(false);
    }
  }, [allCharterChecks]);

  useEffect(() => {
    if (isEditMode && editRegistrationData?.registration) {
      const { registration } = editRegistrationData;
      if (registration.email) setValue('email', registration.email);
      if (registration.company) setValue('company', registration.company);
      if (registration.region) setValue('region', registration.region);
      if (registration.sign) setValue('sign', registration.sign);
      if (registration.exhibition) setValue('exhibition', registration.exhibition);
      if (registration?.mainUser?.profile?.firstName) setValue('profile.firstName', registration.mainUser.profile.firstName);
      if (registration?.mainUser?.profile?.lastName) setValue('profile.lastName', registration.mainUser.profile.lastName);
      if (registration?.mainUser?.userFunction) setValue('userFunction', registration.mainUser.userFunction);
      if (registration?.mainUser?.presences) setValue('presences', registration.mainUser.presences);
    }
  }, [editRegistrationData, isEditMode, setValue]);

  const initialCollaborators = useMemo(() => {
    if (!isEditMode || !editRegistrationData?.registration?.collaborators) return [];
    return editRegistrationData.registration.collaborators;
  }, [editRegistrationData, isEditMode]);

  return (
    <div className={styles.registerForm}>
      <div className={styles.displaySettingsRow}>
        <DisplaySettings placement="inline" />
      </div>
      <h2>Je remplis mes informations personnelles</h2>
      {type === 'magasin' && (
        <>
          <div className={styles.row}>
            <div className={styles.containerField}>
              <StyledSelect
                label="Région"
                name='region'
                control={control}
                options={regions}
                rules={{
                  required: 'Ce champs est obligatoire',
                }}
              />
              {errors?.region && <ErrorField message={errors?.region?.message} />}
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.containerField}>
              <StyledSelect
                label="Enseigne"
                name='sign'
                control={control}
                isSearchable
                options={signs}
                rules={{
                  required: 'Ce champs est obligatoire',
                }}
              />
              {errors?.sign && <ErrorField message={errors?.sign?.message} />}
            </div>
          </div>
        </>
      )}
      {type !== 'magasin' && (
        <div className={styles.row}>
          <div className={styles.containerField}>
            <StyledSelect
              label={type === 'collaborateur' ? 'Activité' : 'Salon' }
              name='exhibition'
              control={control}
              options={type === 'collaborateur' ? [...exhibitions, { label: 'Transverse', value: 'all' }] : exhibitions}
              rules={{
                required: 'Ce champs est obligatoire',
              }}
            />
            {errors?.exhibition && <ErrorField message={errors?.exhibition?.message} />}
          </div>
        </div>
      )}
      {type !== 'collaborateur' && (
        <div className={styles.row}>
          <div className={styles.containerField}>
            {type === 'fournisseur' && (
              <>
                <span>Nom commercial<sup>*</sup></span>
                <p style={{ marginBottom: '10px' }}>
                  Saisissez les premières lettres de votre société puis sélectionnez dans la liste.
                  <br/>
                  Je ne trouve pas ma société : {CONTACT_EMAIL}
                </p>
                <StyledSelect
                  // label="Société"
                  name='company'
                  // disabled={createCompanyLoading}
                  isSearchable
                  control={control}
                  options={optionsCompanies}
                  isClearable
                  // createLabel='Créer la société: '
                  // onCreateOption={(value: string) => handleCreateCompanyFournisseur(value)}
                  rules={{
                    required: 'Ce champs est obligatoire',
                  }}
                />
              </>
            )}
            {type === 'magasin' && (
              <>
                <span>Magasin<sup>*</sup></span>
                <p className={styles.paragraphMailto}>
                  Saisissez votre <span className={styles.bold}>code magasin</span>
                  {' '}
                  ou les premières lettres du magasin puis sélectionnez-le dans la liste.
                  {' '}
                  (ex. : 30976 ou AIZENAY).
                  {' '}
                  <br/>Je ne trouve pas mon magasin : {CONTACT_EMAIL}
                </p>
                <StyledSelect
                  // label="Magasin"
                  name='company'
                  control={control}
                  options={optionsCompanies}
                  isSearchable
                  rules={{
                    required: 'Ce champs est obligatoire',
                  }}
                />
              </>
            )}
            {errors?.company && <ErrorField message={errors?.company?.message} />}
          </div>
        </div>
      )}
      <div className={styles.row}>
        <div className={styles.containerField}>
          <InputText
            label="Prénom"
            name='profile.firstName'
            control={control}
            maxlength={PROFILE_FIRST_LAST_NAME_MAX_LENGTH}
            rules={{
              required: 'Ce champs est obligatoire',
              maxLength: {
                value: PROFILE_FIRST_LAST_NAME_MAX_LENGTH,
                message: `${PROFILE_FIRST_LAST_NAME_MAX_LENGTH} caractères maximum`,
              },
            }}
          />
        </div>
        <div className={styles.containerField}>
          <InputText
            label="Nom"
            name='profile.lastName'
            control={control}
            maxlength={PROFILE_FIRST_LAST_NAME_MAX_LENGTH}
            rules={{
              required: 'Ce champs est obligatoire',
              maxLength: {
                value: PROFILE_FIRST_LAST_NAME_MAX_LENGTH,
                message: `${PROFILE_FIRST_LAST_NAME_MAX_LENGTH} caractères maximum`,
              },
            }}
          />
        </div>
      </div>
      <div className={styles.row}>
        {type === 'magasin' && (
          <div className={styles.containerField}>
            <StyledSelect
              label="Fonction"
              name='userFunction'
              control={control}
              rules={{
                required: 'Ce champs est obligatoire',
              }}
              options={userFunctions}
            />
            {errors?.userFunction && <ErrorField message={errors?.userFunction?.message} />}
          </div>
        )}
        {(type === 'collaborateur' || type === 'fournisseur') && (
          <div className={styles.containerField}>
            <InputText
              label="Fonction"
              name='userFunction'
              control={control}
              rules={{
                required: 'Ce champs est obligatoire',
                maxLength: {
                  value: 44,
                  message: '44 caractères maximum',
                },
              }}
            />
            {errors?.userFunction && <ErrorField message={errors?.userFunction?.message} />}
            {watch('userFunction')?.length > 44 && <ErrorField message={'La fonction ne doit pas dépasser 44 caractères'} />}
          </div>
        )}

        <div className={styles.containerField}>
          <InputEmail
            label="E-mail"
            name='email'
            placeholder='adresse@mail.fr'
            control={control}
            required
          />
          {errors?.email && <ErrorField message={errors?.email?.message} />}
        </div>
      </div>
      {days.length > 0 && (
        <PresencesForm
          control={control}
          setValue={setValue}
          from='register'
          days={days}
          errors={errors.presences || []}
        />
      )}

      {type === 'magasin' && formations.length > 0 && (
        <div className={styles.event}>
          Après confirmation de votre inscription au salon,
          vous recevrez votre badge et les informations pratiques par email.
        </div>
      )}

      {(type === 'magasin' || type === 'fournisseur') && (
        <div className={styles.collaborators}>
          <CollaboratorsForm ref={collaboratorsForm} initialCollaborators={initialCollaborators} />
        </div>
      )}
      <div className={styles.optin}>
        <div className={styles.checkbox}>
          <Checkbox
            name='optin'
            control={control}
            defaultValue={false}
            rules={{
              required: true,
            }}
          />
        </div>
        <p>
          <span className={styles.optinLead}>
            J’accepte de recevoir du courriel de la part de l’organisateur
            dans le cadre de mon inscription.
          </span>
          <span className={styles.dataMention}>
            <br />
            <br />Les données collectées via ce formulaire
            (email, nom, prénom, fonction, entreprise)
            sont traitées par COOPERATIVE U, responsable de traitement.
            <br />La finalité de ce traitement est la gestion des inscriptions au
            salon U bazar 2026.
            La base légale de ce traitement est l&apos;intérêt légitime.
            <br />Vos données seront conservées pendant une durée de 3 ans.
            <br />A l&apos;issue de ce délai, vos données seront anonymisées.
            <br />Vos données seront traitées par COOPERATIVE U et ne seront transmises à des tiers
            externes au Groupement U que pour la gestion des inscriptions à
            l&apos;événement (société Bakasable).
            <br />
            <br />Conformément à la réglementation sur les données personnelles, vous disposez
            d&apos;un
            droit d&apos;accès, de rectification, d&apos;effacement et de portabilité de vos
            données.
            Pour exercer vos droits ou pour toute information, vous pouvez contacter le Délégué
            à la Protection des Données (DPO) de COOPERATIVE U :
            <br />- Par email : dpo_uenseigne@cooperative-u.fr ;
            <br />- Par courrier : COOPERATIVE U - DPO - 20 rue d&apos;Arcueil 94150 Rungis.
            <br />Vous disposez également du droit d&apos;introduire une réclamation auprès de
            l&apos;autorité
            compétente (CNIL) si vous considérez que vos droits n&apos;ont pas été respectés.
          </span>
        </p>
      </div>
      {needsCharterAcceptance && (
        <div className={styles.charterSection}>
          <p className={styles.charterInstruction}>
            Veuillez lire et accepter la charte ci-dessous :
          </p>
          <div className={styles.charterBox}>
            <div className={styles.charterScroll}>
              <img
                src={charteImage}
                alt="Charte d’Engagement Solidaire — Salon Coopérative U"
                className={styles.charterHeaderImg}
              />
              {type === 'magasin' && MAGASIN_CHARTER_LABELS.map((label, index) => {
                const name = `charterCommit${index + 1}` as const;
                return (
                  <div key={name} className={styles.charterCheckboxRow}>
                    <Checkbox
                      name={name}
                      control={control}
                      className={styles.charterCheckbox}
                      defaultValue={false}
                      inline
                      label={label}
                    />
                  </div>
                );
              })}
              {type === 'fournisseur' && FOURNISSEUR_CHARTER_LABELS.map((label, index) => {
                const name = `charterCommit${index + 1}` as const;
                return (
                  <div key={name} className={styles.charterCheckboxRow}>
                    <Checkbox
                      name={name}
                      control={control}
                      className={styles.charterCheckbox}
                      defaultValue={false}
                      inline
                      label={label}
                    />
                  </div>
                );
              })}
            </div>
            <div className={styles.charterBoxFooter}>
              <div className={styles.charterFooterNotes}>
                <p>
                  Vous devez lire et cocher la charte pour pouvoir l’accepter.
                </p>
                <p>
                  Cet engagement vaut également pour les collaborateurs inscrits sur cette page.
                </p>
              </div>
              <button
                type="button"
                className={styles.charterAcceptBtn}
                disabled={!allCharterChecks}
                onClick={() => setCharterAccepted(true)}
              >
                {charterAccepted && (
                  <span className={styles.charterAcceptTick} aria-hidden="true">
                    ✓
                  </span>
                )}
                ACCEPTER
              </button>
            </div>
          </div>
        </div>
      )}
      <p><sup>*</sup>Mentions obligatoires</p>
      {errorApiMessage && <ErrorField message={errorApiMessage} />}
      {captchaError && <ErrorField message={captchaError} />}

      {!isEditMode && RECAPTCHA_SITE_KEY && (
        <>
          <p className={styles.recaptchaHint}>
            Veuillez cocher la case ci-dessous pour continuer
          </p>
          <div className={styles.recaptcha}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              hl="fr"
              theme={isDarkMode ? 'dark' : 'light'}
              onChange={(captchaToken: string | null) => {
                setRecaptchaVerified(!!captchaToken);
                setCaptchaError('');
              }}
            />
          </div>
        </>
      )}

      <div className={styles.containerBtn}>
        <span
          className={styles.submitTooltipWrap}
          data-tooltip={isSubmitDisabled ? submitDisabledReason : undefined}
        >
          <button
            disabled={isSubmitDisabled}
            onClick={handleSubmit(submit)}
          >
            {isEditMode ? 'Je valide mes modifications' : 'Je m\'inscris'}
          </button>
        </span>
      </div>
      {isSuccess && <p className={styles.success}>
        Votre inscription a bien été prise en compte.<br />
        Votre badge a été envoyé par email.
      </p>}
      <div className={styles.mention}>
        <p>
          À la suite de votre inscription, vous pourrez télécharger votre badge d’entrée.<br />
          <span>
            Vous rencontrez un problème lors de l’inscription ?
            {' '}
            Contactez-nous :
            {' '}
            {CONTACT_EMAIL}
          </span>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;
